import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import withStyles from '@material-ui/core/styles/withStyles';

import { connect } from 'react-redux';
import { compose } from 'redux';
import { setHighlightedFeatures, unhighlightFeatures } from '../../redux/highlights/Actions';
import { hidePrimaryPanel, showPrimaryPanel } from '../../redux/panel/Actions';
import { setCurrentPolygonIds } from '../../redux/parcel/Actions';
import { removeActiveLayer, setOrCreateNewActiveLayer, setOrCreateNewActiveLayers } from '../../redux/active_layers/Actions';
import { toggleLandAssemblyFeaturesAsync } from '../../redux/land_assembly/Actions';
import { setMapProperties } from '../../redux/map/Actions';
import { MAIN_MAP } from '../../redux/map/Constants';
import * as PanelNames from '../CartoLib/Panels/PanelNames';

import { CompassIcon } from '../Mapbox/Icons';
import Rotate3dIcon from 'mdi-react/Rotate3dIcon';

import DrawControl from '../Mapbox/controls/DrawControl';
import BaseMapControl from '../Mapbox/controls/BaseMapControl';
import LegendControl from '../Mapbox/controls/LegendControl';

import Markers from '../Mapbox/controls/Markers';
import MapButton from '../Mapbox/controls/Button';

import withDrawControl from '../../hoc/map/WithDrawControl';
import withMapInteraction from '../../hoc/map/WithMapInteraction';
import withSearchResults from '../../hoc/map/WithSearchResults';
import withOwnershipSearchResults from '../../hoc/map/WithOwnershipSearchResults';
import withSiteCards from '../../hoc/map/WithSiteCards';
import withPlanning from '../../hoc/map/WithPlanning';
import StandaloneMap from '../Mapbox/StandaloneMap';

import QueryFeatureInfoBox from './panels/QueryFeatureInfoBox';
import MapContextMenu from './MapContextMenu';

const Map = compose(
    withMapInteraction,
    withOwnershipSearchResults,
    withSearchResults,
    withSiteCards,
    withPlanning,
    withDrawControl
)(StandaloneMap);

class MainMap extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        padding: PropTypes.object,
        ControlContainer: PropTypes.any,
        onMapLoaded: PropTypes.func,
        // Redux state
        isCreatingSiteCard: PropTypes.bool.isRequired,
        cameraOptions: PropTypes.object,
        layers: PropTypes.array,
        baseLayer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
        isMapLoaded: PropTypes.bool.isRequired,
        isDrawLoaded: PropTypes.bool.isRequired,
        drawModule: PropTypes.any,
        drawnFeatures: PropTypes.array,
        homeBookmark: PropTypes.object,
        highlightsRedux: PropTypes.object,
        // Redux dispatch
        dispatchShowPrimaryPanel: PropTypes.func.isRequired,
        dispatchHidePrimaryPanel: PropTypes.func.isRequired,
        dispatchRemoveActiveLayer: PropTypes.func.isRequired,
        dispatchSetCurrentPolygonIds: PropTypes.func.isRequired,
        dispatchSetNewActiveLayer: PropTypes.func.isRequired,
        dispatchToggleLandAssemblyFeatures: PropTypes.func.isRequired,
        setHighlightedFeatures: PropTypes.func.isRequired,
        dispatchUnHighlightFeature: PropTypes.func.isRequired,
    };

    static defaultProps = {
        ControlContainer: null,
        padding: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        }
    };

    constructor(props) {
        super(props);
        this.state = {
            mapInfoBoxOpen: false,
            contextMenu: null,
        };

        this.handleMapClick = this.handleMapClick.bind(this);
        this.handleDrawLoaded = this.handleDrawLoaded.bind(this);
        this.handleMapLoad = this.handleMapLoad.bind(this);
        this.handleContextMenu = this.handleContextMenu.bind(this);

        this.handleThreeD = this.handleThreeD.bind(this);

        this.geolocateControl = null;

        this.map = null;
        this.toggleTerrain = this.toggleTerrain.bind(this);
    }

    componentDidUpdate (prevProps, prevState) {
        const {
            drawnFeatures,
            dispatchSetNewActiveLayer,
            dispatchRemoveActiveLayer,
        } = this.props;

        if (prevProps.drawnFeatures.length === 0 && drawnFeatures.length !== 0) {
            dispatchSetNewActiveLayer({
                uid: 'user_drawing',
                visible: false,
                printable: true,
                pinned: true,
                ignoreAttribution: true,
                userDrawing: true
                //ignoreRender to add later??
            });
        } else if (prevProps.drawnFeatures.length > 0 && drawnFeatures.length === 0) {
            dispatchRemoveActiveLayer('user_drawing')
        }
    }


    handleMapLoad (map, evt) {
        const { onMapLoaded } = this.props;

        // This should be moved to hoc
        // this.geolocateControl = new GeolocateControl({})
        // map.addControl(this.geolocateControl);

        this.map = map;
        onMapLoaded && onMapLoaded();
    }

    handleMapClick (map, evt) {
        const { drawModule, isAssembly } = this.props;

        const drawMode = drawModule && drawModule.drawControl.getMode();

        if (drawMode === 'simple_select' || drawMode === 'direct_select') {
            if (isAssembly) {
                this.handleMapClickLA(map, evt);
            } else {
                this.handleMapClickOriginal(map, evt);
            }
        }
    }

    // Hardcoded mode functionality if in land assembly mode
    handleMapClickLA = (map, evt) => {
        const {
            landAssemblyLayer,
            dispatchToggleLandAssemblyFeatures,
            isCreatingSiteCard
        } = this.props;

        // Is site card creation mode, ignore any other click events as just the draw controls should work
        if (isCreatingSiteCard) { return; }

        const points = map.queryRenderedFeatures(evt.point);

        if (!landAssemblyLayer) { return; }
        const idProperty = landAssemblyLayer.properties.id;

        const toggleFeatures = [];
        for (const point of points) {
            const layerSourceId = point.layer.source;
            // ignore default map box layer
            if (layerSourceId === 'composite') continue;
            // ignore drawing layer
            if (layerSourceId === 'mapbox-gl-draw-cold' || layerSourceId === 'mapbox-gl-draw-hot') {
                this.setState({ mapInfoBoxOpen: true });
                continue;
            }
            // ignore highlight layers
            if (point.layer.id.startsWith('highlight_')) continue;
            if (landAssemblyLayer.system_name !== point.layer['source-layer']) continue;
            const featureId = point.properties[idProperty];
            if (featureId === undefined) {
                console.log(`WARNING: cannot select feature as it has no value for its '${idProperty}' ID property`);
                continue;
            }
            // Points returned can include duplicates, appears to be related to Mapbox tile boundaries and buffers. So prevent duplicates here.
            // https://docs.mapbox.com/mapbox-gl-js/api/map/#map#queryrenderedfeatures
            if (toggleFeatures.find(f => f.featureId === featureId)) { continue; }

            toggleFeatures.push({
                featureId: featureId,
                properties: point.properties,
                lngLat: evt.lngLat
            });
        }

        dispatchToggleLandAssemblyFeatures(toggleFeatures);
    };

    handleMapClickOriginal = (map, evt) => {
        const {
            setHighlightedFeatures,
            dispatchUnHighlightFeature,
            dispatchShowPrimaryPanel,
            dispatchHidePrimaryPanel,
            dispatchSetCurrentPolygonIds,
            highlightsRedux
        } = this.props;

        const points = map.queryRenderedFeatures(evt.point);
        const highlightedFeatures = []; // array of { layerSystemName, featureId }
        let hmlrPoints = [];

        for (const point of points) {
            const layerSourceId = point.layer.source;

            // ignore default map box layer
            if (layerSourceId === 'composite') continue;
            // ignore drawing layer
            if (layerSourceId === 'mapbox-gl-draw-cold' || layerSourceId === 'mapbox-gl-draw-hot') {
                this.setState({ mapInfoBoxOpen: true });
                continue;
            }
            // ignore highlight layers
            if (point.layer.id.startsWith('highlight_')) continue;

            const layer = this.props.layers.find(e => e.system_name === point.layer['source-layer']);

            if (!layer) { continue; }

            const idProperty = layer.properties.id;
            const featureId = point.properties[idProperty];

            if (featureId === undefined) { console.log(`WARNING: cannot select feature as it has no value for its '${idProperty}' ID property`); } else {
                const feature = {
                    featureId: featureId,
                    properties: point.properties,
                    lngLat: evt.lngLat
                };

                let highlightedLayer = highlightedFeatures.find(e => e.layerSystemName === layer.system_name);

                if (!highlightedLayer) {
                    highlightedLayer = {
                        key: `${layer.system_name}_selected`,
                        layerSystemName: layer.system_name,
                        features: []
                    };
                    highlightedFeatures.push(highlightedLayer);
                }

                highlightedLayer.features.push(feature);

                // Should be driven from config data
                if (layer.system_name === 'hmlr_land_parcels') {
                    hmlrPoints.push(point.properties);
                }
            }
        }

        // Check if the user clicked layer is what was previously clicked and stored in redux, if true
        // unhighlight the layer 
        if (highlightsRedux.highlightedFeatures.some(hf => {
            return hf.features.some(feature => feature.featureId === highlightedFeatures[0]?.features[0]?.featureId)
        })) {
            // Setting hmlrPoints to an empty array allows the HMLR layer to be unselected and hides the info panel
            hmlrPoints = []
            dispatchUnHighlightFeature(highlightsRedux?.highlightedFeatures)
        } else {
            setHighlightedFeatures(highlightedFeatures);
        }

        // Show HMLR panel on click and hide on clickaway
        if (hmlrPoints.length === 0) {
            dispatchHidePrimaryPanel();
        } else {
            dispatchShowPrimaryPanel(PanelNames.propertyInfo);
        }

        // Dirty inline object for sorted hmlr polygons and setting them
        const sortedHmlrPoints = _.uniqWith(hmlrPoints, (x, y) => x.polygon_id === y.polygon_id).sort((x, y) => {
            const tenures = ['Freehold', 'Leasehold', 'Unregistered', 'Unknown'];
            const tenureIndexValue = tenures.findIndex(e => e === x.tenure_aggregate) - tenures.findIndex(e => e === y.tenure_aggregate);
            if (tenureIndexValue !== 0) {
                return tenureIndexValue;
            }
            return x.polygon_id < y.polygon_id;
        });

        dispatchSetCurrentPolygonIds(sortedHmlrPoints.map(e => ({
            polygonId: e['polygon_id'],
            titleNo: e['title_no']
        })));

    };

    handleDrawLoaded (draw) {
        const {
            layers,
            homeBookmark,
            dispatchSetNewActiveLayers
        } = this.props;

        if (homeBookmark) {
            if (homeBookmark.selectedLayers) {
                const activeLayers = homeBookmark.selectedLayers.map(sl => ({
                    uid: sl.systemName,
                    systemName: sl.systemName,
                    visible: true,
                    printable: true
                }));
                dispatchSetNewActiveLayers(activeLayers, layers);
            }
        }
    }

    handleThreeD () {
        // const { isMapLoaded, dispatchSetMapProperties, dispatchSetNewActiveLayer, layers } = this.props;
        // const layer = layers.find(e => e.system_name === 'lyr_openbuildings_uk')
        // const activeLayer = {
        //     uid: layer.system_name,
        //     systemName: layer.system_name,
        //     visible: true,
        // }
        // isMapLoaded && dispatchSetNewActiveLayer(activeLayer, layer)
        this.toggleTerrain();
    }

    toggleTerrain () {
        // Should move part of this to redux to maintain when changing page
        // Would also need to call code on mount if redux has 3d active
        const { dispatchSetMapProperties } = this.props;
        if (this.map.getSource('mapbox-dem-terrain')) {
            dispatchSetMapProperties({
                pitch: [0]
            });
            this.map.setTerrain(null);
            this.map.setFog(null);
            this.map.removeSource('mapbox-dem-terrain');
        } else {
            dispatchSetMapProperties({
                pitch: [60]
            });
            this.map.addSource('mapbox-dem-terrain', {
                'type': 'raster-dem',
                'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
                'tileSize': 512,
                'maxzoom': 14
            });
            // add the DEM source as a terrain layer with exaggerated height
            this.map.setTerrain({ 'source': 'mapbox-dem-terrain', 'exaggeration': 1.0 });
            // add sky styling with `setFog` that will show when the map is highly pitched
            this.map.setFog({
                'horizon-blend': 0.3,
                'color': '#f8f0e3',
                'high-color': '#add8e6',
                'space-color': '#d8f2ff',
                'star-intensity': 0.0
            });
        }
    }

    handleContextMenu (map, evt) {
        this.setState({
            contextMenu: {
                mouseX: evt.point.x,
                mouseY: evt.point.y,
                lngLat: evt.lngLat
            }
        });
    }

    render () {
        const { classes, baseLayer, drawModule, cameraOptions, dispatchSetMapProperties, isMapLoaded, isDrawLoaded, isDrawing } = this.props;
        const { contextMenu } = this.state;
        const ControlContainer = this.props.ControlContainer || ((props) => (<div className={classes.controlContainer} >{props.children}</div>));
        const isLoaded = isMapLoaded && isDrawLoaded;
        return (
            <Map
                id={MAIN_MAP}
                baseLayer={baseLayer}
                // flyToOptions are currently bugged in raw Mapbox and save the state somehow which is used by fitbounds
                // Defaulted to 0 padding to resolve
                // see https://github.com/mapbox/mapbox-gl-js/issues/11831
                // flyToOptions={{
                //     padding: padding
                // }}
                onDrawLoaded={this.handleDrawLoaded}
                drawControls={{}}
                events={{
                    onClick: this.handleMapClick,
                    onStyleData: this.handleMapLoad,
                    onContextMenu: this.handleContextMenu
                }}
                drawModule={drawModule}
            >
                <MapContextMenu contextMenu={contextMenu} onClose={e => this.setState({ contextMenu: null })} />
                {
                    isLoaded && <>
                        <Markers />
                        <QueryFeatureInfoBox />
                        <ControlContainer>
                            {this.props.children}

                            <div className={classes.rightControls}>
                                {/* Turn this into a control instead of an icon button */}
                                <DrawControl draw={drawModule} />
                                <LegendControl />

                                <div className={classes.bottomRightControls}>
                                    <div className={classes.baseMapControlContainer}>
                                        <BaseMapControl />
                                    </div>
                                    <div className={classes.bottomRightControlRow}>
                                        <MapButton dense
                                            onClick={this.handleThreeD}
                                        >
                                            <Rotate3dIcon className={isLoaded && this.map.getSource('mapbox-dem-terrain') && classes.threeDActive} />
                                        </MapButton>
                                        <MapButton
                                            dense
                                            onClick={() => dispatchSetMapProperties({
                                                pitch: [0],
                                                bearing: [0]
                                            })}
                                            style={{ transition: '0.2s', transform: (cameraOptions && cameraOptions.bearing) ? `rotate(${-cameraOptions.bearing}deg)` : '' }}
                                        >
                                            <CompassIcon />
                                        </MapButton>
                                        {/* <MapButton dense
                                        onClick={() => this.geolocateControl._onClickGeolocate()}
                                        >
                                            <GpsFixedIcon/>
                                        </MapButton> */}
                                        <div className={classes.intercomPlaceHolder}>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ControlContainer>
                    </>
                }
            </Map>
        );
    }
}

const style = theme => ({
    controlContainer: {
        pointerEvents: 'none',
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        margin: '20px'
    },
    rightControls: {
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > *': {
            pointerEvents: 'all',
            alignSelf: 'flex-end',
            '&:not(:last-child)': {
                marginBottom: theme.constants.verticalMargin * 0.5
            }
        }
    },
    bottomRightControls: {
        marginTop: 'auto',
        pointerEvents: 'none'
    },
    bottomRightControlRow: {
        display: 'flex',
        columnGap: theme.constants.horizontalMargin,
        justifyContent: 'flex-end',
        alignItems: 'center'
    },
    intercomPlaceHolder: {
        width: '60px',
        height: '60px'
    },
    threeDActive: {
        color: theme.palette.secondary.main
    }
});

const mapStateToProps = (state, ownProps) => {
    const bookmarkId = state.bookmarkReducer.homeBookmarkId;
    const homeBookmark = bookmarkId && state.bookmarkReducer.bookmarks.find(b => b.id === bookmarkId);
    const landAssemblyLayer = state.layers.layers.find(l => l.id === state.landAssembly.layerId);

    return {
        isAssembly: state.panels.panels.landAssembly.enabled,
        landAssemblyLayer: landAssemblyLayer,
        isCreatingSiteCard: state.landAssembly.isCreatingSiteCard,

        cameraOptions: state[MAIN_MAP].cameraOptions,
        baseLayer: state.layers.baseLayer,
        layers: state.layers.layers,
        isMapLoaded: state[MAIN_MAP].isMapLoaded,
        isDrawLoaded: Boolean(state[MAIN_MAP].drawModule),
        drawModule: state[MAIN_MAP].drawModule,
        isDrawing: state[MAIN_MAP].isDrawing,
        homeBookmark: homeBookmark,
        drawnFeatures: state[MAIN_MAP].drawnFeatures.features,
        highlightsRedux: state.highlights,

    };
};

const mapDispatchToProps = dispatch => ({
    dispatchSetMapProperties: (properties) => dispatch(setMapProperties(properties, MAIN_MAP)),
    dispatchSetNewActiveLayer: (layer, styling) => dispatch(setOrCreateNewActiveLayer(layer, styling)),
    dispatchSetNewActiveLayers: (a, l) => dispatch(setOrCreateNewActiveLayers(a, l)),
    dispatchRemoveActiveLayer: (layerId) => dispatch(removeActiveLayer(layerId)),
    setHighlightedFeatures: (highlights) => dispatch(setHighlightedFeatures(highlights)),
    dispatchUnHighlightFeature: (highlight) => dispatch(unhighlightFeatures(highlight)),

    dispatchShowPrimaryPanel: (name) => dispatch(showPrimaryPanel(name)),
    dispatchHidePrimaryPanel: () => dispatch(hidePrimaryPanel()),
    dispatchSetCurrentPolygonIds: (polygonIds) => dispatch(setCurrentPolygonIds(polygonIds)),
    dispatchToggleLandAssemblyFeatures: (featureId) => dispatch(toggleLandAssemblyFeaturesAsync(featureId))
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(style)(MainMap));